import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Input,
  message,
  Modal,
  ModalProps,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { useIntl } from 'react-intl';
import ModuleTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { fadeInKeyframes } from '~src/styles/animations';
import { queryCache, useMutation } from 'react-query';
import { patchTendersRegistrationInfo } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { jsx } from '@emotion/core';

function parseDate(date: string | number | Date | undefined) {
  if (!date) return null;

  let parsed: moment.Moment;

  if (typeof date === 'number') {
    parsed = moment.unix(date);
  } else if (typeof date === 'string') {
    parsed = moment(date, moment.ISO_8601, true);
    if (!parsed.isValid()) {
      parsed = moment(date, ['DD-MM-YYYY HH:mm', 'DD-MM-YYYY']);
    }
  } else {
    parsed = moment(date);
  }

  return parsed;
}

function parseToSeconds(date: moment.Moment | null) {
  if (!date) return null;
  return date.unix();
}

const format = 'DD/MM/YYYY - HH:mm';

function getDisabledTime(min?: moment.Moment, max?: moment.Moment) {
  return (current: moment.Moment | null) => {
    if (!current) return {};

    const curDate = current.clone();
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    let disabledHours: number[] = [];
    let disabledMinutes: ((hour: number) => number[]) | undefined;

    if (min && curDate.isSame(min, 'day')) {
      const minHour = min.hour();
      const minMinute = min.minute();

      disabledHours = hours.filter((h) => h < minHour);

      disabledMinutes = (hour: number) =>
        hour === minHour ? minutes.filter((m) => m < minMinute) : [];
    }

    if (max && curDate.isSame(max, 'day')) {
      const maxHour = max.hour();
      const maxMinute = max.minute();

      disabledHours = [...disabledHours, ...hours.filter((h) => h > maxHour)];

      const prevDisabled = disabledMinutes;
      disabledMinutes = (hour: number) => {
        const prev = prevDisabled?.(hour) ?? [];
        const moreThan =
          hour === maxHour ? minutes.filter((m) => m > maxMinute) : [];
        return [...new Set([...prev, ...moreThan])];
      };
    }

    return {
      disabledHours: () => Array.from(new Set(disabledHours)),
      disabledMinutes: disabledMinutes ?? (() => []),
    };
  };
}

export const DatesModalWindow = (
  props: Pick<ModalProps, 'visible' | 'onOk' | 'onCancel'> & {
    item: {
      id?: string;
      reference?: string;
      title?: string;
      projectId?: string;
      tenderId?: string;
      tableEntries: {
        active_from?: string;
        deadline_at?: string;
        publication_date?: string;
        active_until?: string;
        qa_start?: string;
        qa_stop?: string;
        qa_on?: boolean;
        results_deadline?: string;
        results_details?: string;
        execution_period?: string;
        execution_period_data?: string;
      };
    };
  }
) => {
  const intl = useIntl();
  const [active_from, setActive_from] = useState<moment.Moment | null>();
  const [deadline, setDeadline] = useState<moment.Moment | null>();
  const [
    publication_date,
    setPublication_date,
  ] = useState<moment.Moment | null>();
  const [active_until, setActive_until] = useState<moment.Moment | null>();
  const [qa_start, setQa_start] = useState<moment.Moment | null>();
  const [qa_stop, setQa_stop] = useState<moment.Moment | null>();
  const [qa_on, setQa_on] = useState<boolean>();
  const [
    results_deadline,
    setResults_deadline,
  ] = useState<moment.Moment | null>();
  const [results_details, setResults_details] = useState<string>();
  const [execution_period, setExecution_period] = useState<string>();
  const [
    execution_period_data,
    setExecution_period_data,
  ] = useState<moment.Moment | null>();

  const [isModalLoading, setIsModalLoading] = useState(false);
  const [errorFields, setErrorFields] = useState<Record<string, boolean>>({});

  // Note: We keep this state initialization to respect initial data,
  // but we no longer use it to force validation on active_until.
  const [isParticipationEnabled, setIsParticipationEnabled] = useState<boolean>(
    !!props.item.tableEntries.active_until
  );

  // Init once per "open session", not on every refetch
  const didInitRef = useRef(false);

  useEffect(() => {
    // When modal closes -> allow init next time it opens
    if (!props.visible) {
      didInitRef.current = false;
      return;
    }

    // Only reset form when modal opens, not when data refetches
    // This prevents React Query's refetch interval from clearing user input
    if (props.visible && !didInitRef.current) {
      setActive_from(parseDate(props.item.tableEntries.active_from));
      setPublication_date(parseDate(props.item.tableEntries.publication_date));

      // const parsedActiveFrom = parseDate(props.item.tableEntries.active_from);
      // let parsedDeadline = parseDate(props.item.tableEntries.deadline);
      //
      // if (!parsedDeadline) {
      //   if (parsedActiveFrom) {
      //     parsedDeadline = parsedActiveFrom.clone().add(7, 'days');
      //   } else {
      //     parsedDeadline = moment().add(7, 'days');
      //   }
      // }
      // setDeadline(parsedDeadline);

      const parsedActiveUntil = parseDate(props.item.tableEntries.active_until);
      setActive_until(parsedActiveUntil);
      setIsParticipationEnabled(!!parsedActiveUntil);

      const parsedQAStart = parseDate(props.item.tableEntries.qa_start);
      const parsedQAStop = parseDate(props.item.tableEntries.qa_stop);
      setQa_start(parsedQAStart);
      setQa_stop(parsedQAStop);
      setQa_on(props.item.tableEntries.qa_on);

      setDeadline(parseDate(props.item.tableEntries.deadline_at));
      setResults_deadline(parseDate(props.item.tableEntries.results_deadline));
      setResults_details(props.item.tableEntries.results_details ?? '');
      setExecution_period(props.item.tableEntries.execution_period ?? '');

      setExecution_period_data(() => {
        const raw = props.item.tableEntries.execution_period_data;
        if (raw == null) return null;
        const num = Number(raw);
        const year = Math.floor(num);
        const quarter = Math.round((num - year) * 10);
        if (!year || !quarter || quarter < 1 || quarter > 4) return null;
        return moment().year(year).quarter(quarter).startOf('quarter');
      });

      didInitRef.current = true;
    }
  }, [props.visible, props.item, props.item.tableEntries]);

  const [doSaveTenderDates] = useMutation(patchTendersRegistrationInfo, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'tender-dates-save-error' }));
      setIsModalLoading(false);
    },
    onSuccess: (data, variables) => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      setIsModalLoading(false);

      props.onOk &&
        props.onOk(
          (null as unknown) as React.MouseEvent<HTMLElement, MouseEvent>
        );
    },
  });

  const validateFields = () => {
    const errors: Record<string, boolean> = {};
    let errorMsg = '';

    // 1. Validate Mandatory Fields
    if (!deadline) {
      errors.deadline = true;
      errorMsg = intl.formatMessage({
        id: 'tender-dates-error-deadline-required',
      });
    }

    // UPDATED: Participation deadline (active_until) is now optional.
    // if (isParticipationEnabled && !active_until) {
    //   errors.active_until = true;
    //   errorMsg = 'Participation deadline is required.';
    // }

    // 2. Validate Participation Deadline (active_until)
    if (active_until) {
      // Cannot be before active_from
      if (active_from && active_until.isBefore(active_from, 'minute')) {
        errors.active_until = true;
        errorMsg = intl.formatMessage({
          id: 'tender-dates-error-active-until-before-start',
        });
      }
      // Cannot be after deadline (Bids)
      if (deadline && active_until.isAfter(deadline, 'minute')) {
        errors.active_until = true;
        errorMsg = intl.formatMessage({
          id: 'tender-dates-error-active-until-after-deadline',
        });
      }
    }

    // 3. Validate Bids Deadline (deadline)
    if (deadline) {
      // Cannot be before active_from
      if (active_from && deadline.isBefore(active_from, 'minute')) {
        errors.deadline = true;
        errorMsg = intl.formatMessage({
          id: 'tender-dates-error-deadline-before-start',
        });
      }
    }

    // 4. Validate Q&A Dates (only if enabled)
    if (qa_on) {
      // UPDATED: QA Start and Stop are now optional.
      // if (!qa_start) {
      //   errors.qa_start = true;
      //   errorMsg = 'Q&A Start date is required.';
      // }
      // if (!qa_stop) {
      //   errors.qa_stop = true;
      //   errorMsg = 'Q&A Stop date is required.';
      // }

      if (qa_start && qa_stop) {
        // Stop must be after Start
        if (qa_stop.isSameOrBefore(qa_start, 'minute')) {
          errors.qa_stop = true;
          errorMsg = intl.formatMessage({ id: 'qa-error-stop-before-start' });
        }
      }

      if (qa_start) {
        // Start cannot be before active_from
        if (active_from && qa_start.isBefore(active_from, 'minute')) {
          errors.qa_start = true;
          errorMsg = intl.formatMessage({
            id: 'tender-dates-error-qa-start-before-start',
          });
        }
      }

      if (qa_stop) {
        // Stop cannot be after Bids Deadline
        if (deadline && qa_stop.isAfter(deadline, 'minute')) {
          errors.qa_stop = true;
          errorMsg = intl.formatMessage({ id: 'qa-error-stop-after-deadline' });
        }
      }
    }

    // 5. Validate Results Announcement (results_deadline)
    if (results_deadline && deadline) {
      // Must be strictly AFTER the Bids Deadline
      if (results_deadline.isSameOrBefore(deadline, 'minute')) {
        errors.results_deadline = true;
        errorMsg = intl.formatMessage({
          id: 'tender-dates-error-results-after-deadline',
        });
      }
    }

    // 6. Validate Execution Period Quarter (execution_period_data)
    if (execution_period_data) {
      // Ensure the quarter isn't ending before the tender even starts
      if (
        active_from &&
        execution_period_data.endOf('quarter').isBefore(active_from, 'day')
      ) {
        // Just a warning usually, but here strict validation
        // You can decide if you want to block this or not.
        // errors.execution_period_data = true;
      }

      // Ensure the quarter isn't starting way after valid dates (logic depends on business rules)
    }

    // If we found an error message, show it (only shows the last found error, which is usually fine)
    if (errorMsg) {
      message.error(errorMsg);
    }

    setErrorFields(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    // if (!deadline) {
    //   if (active_from) {
    //     setDeadline(active_from.clone().add(7, 'days'));
    //   } else {
    //     setDeadline(moment().add(7, 'days'));
    //   }
    // }

    // if (!active_until) {
    //   setActive_until(deadline?.clone());
    // }

    if (!deadline) return;

    // if (!qa_start) {
    //   setQa_start(publication_date ?? active_from?.clone());
    // }
    //
    // if (!qa_stop) {
    //   setQa_stop(deadline ?? active_until?.clone());
    // }

    // if (!results_deadline) {
    //   const base = qa_stop ?? active_until;
    //   if (base && base.isBefore(active_until)) {
    //     setResults_deadline(active_until.clone().add(1, 'minute')); // гарантія що пізніше
    //   } else if (base) {
    //     setResults_deadline(base.clone());
    //   }
    // }
    //
    // if (!execution_period_data) {
    //   const quarterMoment = active_until.clone().startOf('quarter');
    //   if (
    //     quarterMoment.isSameOrAfter(active_from, 'day') &&
    //     quarterMoment.isSameOrBefore(active_until, 'day')
    //   ) {
    //     setExecution_period_data(quarterMoment);
    //   }
    // }
  }, [
    active_from,
    active_until,
    qa_start,
    qa_stop,
    execution_period_data,
    results_deadline,
  ]);

  const Title = () => (
    <div>
      <NavIcon
        customStyle={{ width: '45px', height: '22px' }}
        icon={ModuleTenderHammerNoColor}
      />
      {intl.formatMessage({ id: 'tender-details-edit-dates' })}
    </div>
  );

  return (
    <Modal
      title={<Title />}
      visible={props.visible}
      destroyOnClose={true}
      maskClosable={false}
      okText={intl.formatMessage({ id: 'save' })}
      onOk={async () => {
        if (!props.item.projectId || !props.item.tenderId) return;

        if (!validateFields()) {
          return;
        }

        // Create the full payload
        const rawPayload = {
          projectId: props.item.projectId,
          tenderId: props.item.tenderId,
          results_details: results_details,
          active_from: parseToSeconds(active_from!),
          qa_on: qa_on,
          qa_start: qa_on ? parseToSeconds(qa_start!) : null,
          qa_stop: qa_on ? parseToSeconds(qa_stop!) : null,
          deadline_at: parseToSeconds(deadline!),
          results_deadline: results_deadline
            ? parseToSeconds(results_deadline!)
            : null,
          active_until: parseToSeconds(active_until!),
          execution_period: execution_period,
          execution_period_data: execution_period_data
            ? parseFloat(
                `${execution_period_data.year()}.${execution_period_data.quarter()}`
              )
            : null,
        };

        // 2. Filter out null or undefined values, BUT allow explicit nulls for optional fields
        const filteredPayload = Object.fromEntries(
          Object.entries(rawPayload).filter(([key, value]) => {
            // We allow nulls for qa_start/qa_stop/active_until to ensure they are cleared if the user removed them.
            if (['qa_start', 'qa_stop', 'active_until'].includes(key)) {
              return true;
            }

            if (key === 'results_deadline') {
              return true; // Keep nulls for QA fields if QA is disabled
            }
            if (key === 'execution_period_data') {
              return true; // Keep nulls for QA fields if QA is disabled
            }
            return value !== null && value !== undefined;
          })
        );

        //@ts-ignore
        await doSaveTenderDates(filteredPayload);
      }}
      onCancel={props.onCancel}
      confirmLoading={isModalLoading}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Row style={{ display: 'flex', flex: 1 }}>
          <Col span={6}>
            {props.item.reference ? (
              <Typography>
                {intl.formatMessage({ id: 'contactor-ref-number' })}
              </Typography>
            ) : null}
            {props.item.title ? (
              <Typography>{intl.formatMessage({ id: 'tender' })}:</Typography>
            ) : null}
          </Col>
          <Col>
            {props.item.reference ? (
              <Typography style={{ fontWeight: 700 }}>
                {props.item.reference ??
                  intl.formatMessage({ id: 'not-found-id' })}
              </Typography>
            ) : null}
            {props.item.title ? (
              <Typography style={{ fontWeight: 700 }}>
                {props.item.title?.slice(0, 50) ??
                  intl.formatMessage({ id: 'not-found-title' })}
              </Typography>
            ) : null}
          </Col>
        </Row>
        <IconPopup
          headerId={'tender_edit_dates_help_modal_header'}
          messageId={'tender_edit_dates_help_modal_message'}
          customStyle={{ fontSize: '130%' }}
        />
      </Row>

      {/* Row 1: Activation Date and Deadline for Bids */}
      <Row
        style={{
          marginTop: '30px',
          marginBottom: '10px',
          justifyContent: 'space-between',
        }}>
        <Col span={11}>
          {/*<label*/}
          {/*  style={{*/}
          {/*    display: 'flex',*/}
          {/*    alignItems: 'center',*/}
          {/*    justifyContent: 'space-between',*/}
          {/*  }}>*/}
          {/*  <span>*/}
          {/*    <Tooltip*/}
          {/*      title={'Required'}*/}
          {/*      placement="top"*/}
          {/*      mouseEnterDelay={0.5}*/}
          {/*      trigger={['hover']}>*/}
          {/*      {intl.formatMessage({ id: 'tender-details-active_from' })}*/}
          {/*      <span style={{ color: 'red' }}>*</span>*/}
          {/*    </Tooltip>*/}
          {/*  </span>*/}
          {/*  <IconPopup*/}
          {/*    messageId={'tender_edit_dates_help_activation_message'}*/}
          {/*    headerId={'tender_edit_dates_help_activation_header'}*/}
          {/*  />*/}
          {/*</label>*/}
          {/*<DatePicker*/}
          {/*  style={{*/}
          {/*    minWidth: '50%',*/}
          {/*    marginBottom: 16,*/}
          {/*  }}*/}
          {/*  showTime*/}
          {/*  showNow={false}*/}
          {/*  format={format}*/}
          {/*  value={active_from}*/}
          {/*  onChange={setActive_from}*/}
          {/*  allowClear={true}*/}
          {/*  disabled={!!publication_date}*/}
          {/*/>*/}

          {/* PUBLICATION DATE PICKER */}
          {/*<label>*/}
          {/*  {intl.formatMessage({ id: 'tender-details-publication_date' })}*/}
          {/*  <IconPopup messageId={'#1005-message'} headerId={'#1005-header'} />*/}
          {/*</label>*/}
          {/*<DatePicker*/}
          {/*  style={{*/}
          {/*    minWidth: '50%',*/}
          {/*    marginBottom: 16,*/}
          {/*  }}*/}
          {/*  showTime*/}
          {/*  showNow={false}*/}
          {/*  format={format}*/}
          {/*  // disabledDate={}*/}
          {/*  value={publication_date}*/}
          {/*  onChange={setPublication_date}*/}
          {/*/>*/}

          {/*  ========== */}

          <label
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              {intl.formatMessage({ id: 'tender-details-active_until' })}
            </span>
            <IconPopup
              messageId={'tender_edit_dates_help_paticipation_deadline_message'}
              headerId={'tender_edit_dates_help_paticipation_deadline_header'}
            />
          </label>
          <>
            <DatePicker
              style={{
                minWidth: '100%',
                marginBottom: 16,
                height: 32,
              }}
              showTime
              showNow={false}
              format={format}
              disabledDate={(date) => {
                if (!date) return false;

                const minDate = active_from?.clone().startOf('minute');
                const maxDate = deadline?.clone().startOf('minute');
                const tomorrow = moment().add(1, 'day').startOf('day');

                if (date.isBefore(tomorrow, 'day')) return true;
                if (minDate && date.isBefore(minDate)) return true;
                if (maxDate && date.isAfter(maxDate)) return true;
                return false;
              }}
              disabledTime={getDisabledTime(active_from)}
              value={active_until}
              onChange={setActive_until}
            />

            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 16,
              }}>
              {[-14, -30, -60, -90].map((delta) => {
                const baseDate = active_until ?? deadline;
                const newDate = baseDate?.clone().add(delta, 'days');

                // Calculate the effective Minimum Date
                // We must use the same logic as disabledDate above.
                // It must be at least "Tomorrow" AND at least "active_from"
                const tomorrow = moment().add(1, 'day').startOf('day');
                const minAllowed = active_from
                  ? moment.max(active_from, tomorrow)
                  : tomorrow;

                const isDisabled =
                  !baseDate ||
                  (delta < 0 &&
                    newDate &&
                    newDate.isBefore(minAllowed, 'day')) ||
                  (delta > 0 &&
                    deadline &&
                    newDate.isAfter(deadline, 'minute'));

                return (
                  <div key={delta} style={{ width: 56 }}>
                    <Button
                      size="small"
                      block
                      disabled={isDisabled}
                      style={{
                        padding: '0 4px',
                        minWidth: 0,
                        width: '100%',
                        textAlign: 'center',
                      }}
                      onClick={() => {
                        if (!isDisabled && baseDate) {
                          setActive_until(baseDate.clone().add(delta, 'days'));
                        }
                      }}>
                      {delta > 0 ? `+${delta}d` : `${delta}d`}
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        </Col>
        <Col span={11}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            {intl.formatMessage({ id: 'tender-details-deadline' })}

            <IconPopup
              messageId={'tender_edit_dates_help_deadline_message'}
              headerId={'tender_edit_dates_help_deadline_header'}
            />
          </label>
          <DatePicker
            style={{
              minWidth: '100%',
              marginBottom: 16,
            }}
            showTime
            showNow={false}
            format={format}
            value={deadline}
            onChange={setDeadline}
            disabledDate={(date) => {
              if (!date) return false;

              const today = moment().startOf('day');
              const start = active_from
                ? active_from.clone().startOf('day')
                : today;

              // Logic: Take the LATEST date between "Today" and "Active From"
              // If active_from is in the future, we use it.
              // If active_from is in the past (or null), we use today.
              const effectiveStart = start.isAfter(today) ? start : today;

              // Add 7 days to that effective start date
              const minDate = effectiveStart.clone().add(7, 'days');

              return date.isBefore(minDate, 'day');
            }}
          />
        </Col>
      </Row>

      {/* Row 2: Question and Answer and Deadline for participation */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <span>
          {intl.formatMessage({ id: 'tender-qa' })}
          <IconPopup
            messageId={'tender_edit_dates_help_qa_message'}
            headerId={'tender_edit_dates_help_qa_header'}
          />
        </span>
        <Checkbox
          checked={qa_on}
          onChange={(e) => {
            setQa_on(e.target.checked);
            // if (e.target.checked) {
            //   setQa_start(publication_date ?? active_from?.clone());
            //   setQa_stop(deadline ?? active_until?.clone());
            // }
            // UPDATED: Logic to auto-fill dates is removed.
            // They are optional and should start empty if not already set.
            if (!e.target.checked) {
              setQa_start(null);
              setQa_stop(null);
            }
          }}
          style={{ marginLeft: 8 }}></Checkbox>
      </div>
      <Row
        style={{
          marginTop: '30px',
          marginBottom: '10px',
          justifyContent: 'space-between',
        }}>
        <Col span={11}>
          <div>
            {qa_on && (
              <>
                <label>
                  {intl.formatMessage({ id: 'tender-details-qa_start' })}
                </label>
                <DatePicker
                  style={{ minWidth: '100%', marginBottom: 16, height: 32 }}
                  showTime
                  showNow={false}
                  format={format}
                  value={qa_start}
                  onChange={setQa_start}
                  status={errorFields.qa_start ? 'error' : ''}
                  disabledDate={(date) => {
                    if (!date) return false;
                    const minDateActive = active_from
                      ? active_from.clone().startOf('day')
                      : null;
                    const maxDateQAstop = qa_stop
                      ? qa_stop.clone().startOf('day')
                      : null;

                    const maxDateDeadlineForBids = deadline
                      ? deadline.clone().startOf('day')
                      : null;

                    const today = moment().startOf('day');

                    // if (date.isBefore(today, 'day')) return true;

                    if (
                      maxDateDeadlineForBids &&
                      date.isAfter(maxDateDeadlineForBids, 'day')
                    )
                      return true;
                    if (minDateActive && date.isBefore(minDateActive, 'day'))
                      return true;
                    if (maxDateQAstop && date.isAfter(maxDateQAstop, 'day'))
                      return true;

                    return false;
                  }}
                  // If selecting "Today", disable past hours
                  disabledTime={getDisabledTime(
                    moment(),
                    qa_stop ?? active_until
                  )}
                />
              </>
            )}
          </div>
        </Col>

        <Col span={11}>
          {qa_on && (
            <>
              <label>
                {intl.formatMessage({ id: 'tender-details-qa_stop' })}
              </label>
              <DatePicker
                style={{ minWidth: '100%', marginBottom: 16, height: 32 }}
                showTime
                showNow={false}
                format={format}
                key={deadline ? deadline.unix() : 'qa-stop-empty'}
                defaultPickerValue={deadline || undefined}
                value={qa_stop}
                onChange={setQa_stop}
                status={errorFields.qa_stop ? 'error' : ''}
                disabledDate={(date) => {
                  if (!date) return false;
                  const minDate = qa_start
                    ? qa_start.clone().startOf('day')
                    : null;
                  const maxDate = deadline
                    ? deadline.clone().startOf('day')
                    : null;

                  if (minDate && date.isBefore(minDate, 'day')) return true;
                  if (maxDate && date.isAfter(maxDate, 'day')) return true;
                  return false;
                }}
                // If qa_start is in the future, use that. If qa_start is in the past/null, use Now.
                disabledTime={getDisabledTime(
                  qa_start && qa_start.isAfter(moment()) ? qa_start : moment(),
                  deadline
                )}
              />
            </>
          )}
        </Col>
      </Row>

      {/* Row 3: Announcement of result and Execution period */}
      <Row
        style={{
          marginTop: '30px',
          marginBottom: '10px',
          justifyContent: 'space-between',
        }}>
        <Col span={11}>
          <label>
            {intl.formatMessage({ id: 'tender-details-results_deadline' })}

            <IconPopup
              messageId={'tender_edit_dates_help_announcement_results_message'}
              headerId={'tender_edit_dates_help_announcement_results_header'}
            />
          </label>
          <DatePicker
            style={{
              minWidth: '100%',
              marginBottom: 16,
              height: 32,
            }}
            showTime
            showNow={false}
            format={format}
            key={deadline ? deadline.unix() : 'results-empty'}
            defaultPickerValue={deadline || undefined}
            value={results_deadline}
            onChange={setResults_deadline}
            status={errorFields.results_deadline ? 'error' : ''}
            disabledDate={(date) =>
              deadline
                ? date.isSameOrBefore(deadline, 'day')
                : date.isBefore(moment(), 'day')
            }
          />
          <Input.TextArea
            style={{
              width: '100%',
              animation: `${fadeInKeyframes} 0.3s ease-out`,
            }}
            rows={3}
            placeholder={intl.formatMessage({
              id: 'tender-details-add-description-placeholder',
            })}
            value={results_details}
            onChange={({ target: { value } }) =>
              setResults_details(value.charAt(0).toUpperCase() + value.slice(1))
            }
          />
        </Col>
        <Col span={11}>
          <label>
            {intl.formatMessage({ id: 'tender-details-execution_period' })}
            <IconPopup
              messageId={
                'tender-details-dates-edit-execution-period-help-message'
              }
              headerId={
                'tender-details-dates-edit-execution-period-help-header'
              }
            />
          </label>
          <DatePicker
            style={{
              width: '100%',
              marginBottom: 16,
              height: 32,
            }}
            picker="quarter"
            format="YYYY-[Q]Q"
            key={deadline ? deadline.unix() : 'exec-period-empty'}
            defaultPickerValue={deadline || undefined}
            value={execution_period_data}
            onChange={setExecution_period_data}
            disabledDate={(date) =>
              (active_from &&
                date.endOf('quarter').isBefore(active_from, 'day')) ||
              // || (deadline && date.startOf('quarter').isAfter(deadline, 'day'))
              (deadline && date.endOf('quarter').isBefore(deadline, 'day'))
            }
          />
          <Input.TextArea
            style={{
              width: '100%',
              animation: `${fadeInKeyframes} 0.3s ease-out`,
            }}
            rows={3}
            placeholder={intl.formatMessage({
              id: 'tender-details-add-description-placeholder',
            })}
            value={execution_period}
            onChange={({ target: { value } }) =>
              setExecution_period(
                value.charAt(0).toUpperCase() + value.slice(1)
              )
            }
          />
        </Col>
      </Row>
    </Modal>
  );
};

// {/* PUBLICATION DATE PICKER */}
// {/*<label*/}
// {/* style={{*/}
// {/* display: 'flex',*/}
// {/* alignItems: 'center',*/}
// {/* justifyContent: 'space-between',*/}
// {/* }}>*/}
// {/* <span>*/}
// {/* <Tooltip*/}
// {/* title={'Required'}*/}
// {/* placement="top"*/}
// {/* mouseEnterDelay={0.5}*/}
// {/* trigger={['hover']}>*/}
// {/* {intl.formatMessage({ id: 'tender-details-active_from' })}*/}
// {/* <span style={{ color: 'red' }}>*</span>*/}
// {/* </Tooltip>*/}
// {/* </span>*/}
// {/* <IconPopup*/}
// {/* messageId={'tender_edit_dates_help_activation_message'}*/}
// {/* headerId={'tender_edit_dates_help_activation_header'}*/}
// {/* />*/}
// {/*</label>*/}
// {/*<DatePicker*/}
// {/* style={{*/}
// {/* minWidth: '50%',*/}
// {/* marginBottom: 16,*/}
// {/* }}*/}
// {/* showTime*/}
// {/* showNow={false}*/}
// {/* format={format}*/}
// {/* value={active_from}*/}
// {/* onChange={setActive_from}*/}
// {/* allowClear={true}*/}
// {/* disabled={!!publication_date}*/}
// {/*/>*/}
//

// {/*<label>*/}
// {/* {intl.formatMessage({ id: 'tender-details-publication_date' })}*/}
// {/* <IconPopup messageId={'#1005-message'} headerId={'#1005-header'} />*/}
// {/*</label>*/}
// {/*<DatePicker*/}
// {/* style={{*/}
// {/* minWidth: '50%',*/}
// {/* marginBottom: 16,*/}
// {/* }}*/}
// {/* showTime*/}
// {/* showNow={false}*/}
// {/* format={format}*/}
// {/* // disabledDate={}*/}
// {/* value={publication_date}*/}
// {/* onChange={setPublication_date}*/}
// {/*/>*/}
// {/* ========== */}
